/**
 * useClosedCycleCryoStorage
 *
 * What the most recent closed cycle of one preservation type put into storage,
 * read from the specimens API. Home feeds it to the experience resolver's
 * cryo-inventory signal and to the outcome funnel's frozen counts.
 */

import { useMemo } from "react";
import {
  useCycles,
  type CycleType,
} from "@src/packages/cycles/hooks/useCycles";
import { useFrozenSpecimensForCycle } from "@src/packages/care/cryovault/useSpecimens";

export type PreservationCycleType = Extract<
  CycleType,
  "egg_freezing" | "embryo_freezing" | "sperm_freezing"
>;

export type ClosedCycleCryoStorage = {
  itemCount: number;
  itemType: "eggs" | "embryos" | "sperm";
};

export function useClosedCycleCryoStorage(
  cycleType: PreservationCycleType | null,
): ClosedCycleCryoStorage | undefined {
  const { data: cycles } = useCycles();

  const closedCycleId = useMemo(() => {
    if (!cycleType || !cycles) return null;
    return (
      [...cycles]
        .filter((c) => c.status === "closed" && c.cycleType === cycleType)
        .sort((a, b) =>
          (b.endDate ?? b.createdAt ?? "").localeCompare(
            a.endDate ?? a.createdAt ?? "",
          ),
        )[0]?.id ?? null
    );
  }, [cycles, cycleType]);

  const { cycleTotalEggs, cycleTotalEmbryos, cycleTotalSperm } =
    useFrozenSpecimensForCycle(closedCycleId);

  return useMemo(() => {
    if (!cycleType || !closedCycleId) return undefined;
    if (cycleType === "embryo_freezing") {
      return { itemCount: cycleTotalEmbryos, itemType: "embryos" };
    }
    if (cycleType === "sperm_freezing") {
      return { itemCount: cycleTotalSperm, itemType: "sperm" };
    }
    return { itemCount: cycleTotalEggs, itemType: "eggs" };
  }, [
    closedCycleId,
    cycleTotalEggs,
    cycleTotalEmbryos,
    cycleTotalSperm,
    cycleType,
  ]);
}
